import Head from 'next/head'
import { useRouter } from 'next/router'
import type React from 'react'

// Brand-stable global SEO defaults. Per-page title/description/canonical come from the
// CMS (Pimcore) or Vendure; everything below is site-wide until it can be sourced per-entity.
// TODO(STO-2): SITE_URL-relative values marked CONFIRM should eventually be pulled from a
// Pimcore/Vendure site config so editors can override them. See docs/superpowers/specs.
const SITE_NAME = 'Stocker Kaminsysteme'
const DEFAULT_AUTHOR = 'Stocker Kaminsysteme' // used for both author and publisher
// CONFIRM with Stocker — fallback description for pages without their own seoDescription.
const DEFAULT_DESCRIPTION =
  'Stocker Kaminsysteme – Ihr Spezialist für innovative Schornstein- und Kaminsysteme.'
// TODO(STO-2): set to '/og-default.jpg' once the branded 1200×630 asset is added to public/.
// Kept empty for now so pages without their own image don't emit a 404 og:image.
const DEFAULT_OG_IMAGE = ''
const OG_LOCALE = 'de_DE'
const DEFAULT_ROBOTS = 'index, follow'
// Route prefixes that must never be indexed (account area, checkout funnel, auth).
const NOINDEX_PREFIXES = ['/account', '/checkout', '/cart', '/auth']

interface ISeoPros {
  title: string | null
  description?: string | null
  keywords?: string | null
  canonicals?: Array<{
    absolutePath: string
  }>
  ogImage?: string | null
  ogType?: 'website' | 'article' | 'product'
  noIndex?: boolean
  suffix?: string | null
  /** When true, `title` is already complete (e.g. an editor-authored Pimcore SEO title) and the
   *  site-name `suffix` is NOT appended. Fallback titles leave this false so they still get suffixed. */
  titleIsComplete?: boolean
}

export const Seo: React.FC<ISeoPros> = ({
  title,
  description,
  keywords,
  canonicals,
  ogImage,
  ogType = 'website',
  noIndex = false,
  suffix = '| Stocker Kaminsysteme',
  titleIsComplete = false,
}) => {
  const router = useRouter()

  // SSR-safe: window is undefined during static generation. Fall back to NEXT_PUBLIC_SITE_URL
  // (the storefront's own origin), otherwise emit a relative canonical (valid per HTML5 —
  // browsers resolve against the current document URL).
  const origin = typeof window !== 'undefined'
    ? window.location.origin
    : process.env.NEXT_PUBLIC_SITE_URL ?? ''

  // `||` (not `??`): upstream CMS mapping can yield an empty string, which must still fall back.
  const resolvedDescription = description || DEFAULT_DESCRIPTION
  const resolvedTitle = title || SITE_NAME

  // Absolute-URL guard: a value that already has a scheme is used as-is, otherwise it's
  // resolved against the origin. Applied to both canonical and OG image.
  const toAbsolute = (pathOrUrl: string) =>
    pathOrUrl.startsWith('http') ? pathOrUrl : `${origin}${pathOrUrl}`

  // Self-canonical fallback when no explicit canonical is provided. Strip query + hash.
  // NOTE: assumes single (default) locale — Next i18n strips the locale prefix from asPath,
  // so if additional locales are enabled this must incorporate router.locale.
  const selfPath = router.asPath.split('?')[0].split('#')[0]

  const isPrivateRoute = NOINDEX_PREFIXES.some(prefix => selfPath.startsWith(prefix))
  const robots = noIndex || isPrivateRoute ? 'noindex, nofollow' : DEFAULT_ROBOTS

  // Exactly one canonical (first entry wins) — multiple <link rel=canonical> makes engines ignore all.
  const canonicalUrl = toAbsolute(canonicals?.[0]?.absolutePath ?? selfPath)

  const resolvedImage = ogImage || DEFAULT_OG_IMAGE
  const ogImageUrl = resolvedImage ? toAbsolute(resolvedImage) : null

  return (
    <Head>
      {title && <title>{suffix && !titleIsComplete ? `${resolvedTitle} ${suffix}` : resolvedTitle}</title>}
      <meta name="description" content={resolvedDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      <meta name="author" content={DEFAULT_AUTHOR} />
      <meta name="publisher" content={DEFAULT_AUTHOR} />

      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={OG_LOCALE} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonicalUrl} />
      {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      {ogImageUrl && <meta name="twitter:image" content={ogImageUrl} />}
    </Head>
  )
}
